import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  useTheme
} from '@mui/material';
import {
  FileDownload as ImportIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import internalServiceService from '../../services/internal-service.service';
import { UAE_DEFAULT_CUSTOM_FIELDS } from '../../services/service-field.service';
import { getBrandedTableHeadSx } from '../../utils/colorUtils';

// UAE Standard Template — 25 services + 4 groups (department-based categories)
const UAE_TEMPLATE = {
  version: '1.1',
  name: 'UAE Standard Typing Center',
  services: [
    // ICP — Immigration (5)
    { templateKey: 'UAE-VISA-NEW', name: 'Employment Visa - New', category: 'ICP', serviceChargeIncl: 262.50, govtCharge: 0, description: 'New employment/residence visa application', aliases: ['new visa', 'employment visa', 'new work visa'], requiredDocs: ['PASSPORT_COPY', 'PHOTO', 'OFFER_LETTER', 'MEDICAL_REPORT'] },
    { templateKey: 'UAE-VISA-RENEW', name: 'Employment Visa - Renew', category: 'ICP', serviceChargeIncl: 210.00, govtCharge: 0, description: 'Renewal of employment/residence visa (iqama)', aliases: ['visa renewal', 'iqama renewal', 'iqama', 'renew visa', 'residence visa renewal'], requiredDocs: ['PASSPORT_COPY', 'EMIRATES_ID', 'VISA_PAGE'] },
    { templateKey: 'UAE-VISIT-VISA', name: 'Visit Visa', category: 'ICP', serviceChargeIncl: 157.50, govtCharge: 0, description: 'Visit/tourist visa application', aliases: ['visit visa', 'tourist visa', 'family visa'], requiredDocs: ['PASSPORT_COPY', 'PHOTO'] },
    { templateKey: 'UAE-EXIT-VISA', name: 'Exit Visa', category: 'ICP', serviceChargeIncl: 105.00, govtCharge: 0, description: 'Exit permit processing', aliases: ['exit visa', 'exit permit'], requiredDocs: ['PASSPORT_COPY', 'EMIRATES_ID'] },
    { templateKey: 'UAE-VISA-CANCEL', name: 'Visa Cancellation', category: 'ICP', serviceChargeIncl: 157.50, govtCharge: 0, description: 'Visa cancellation processing', aliases: ['cancel visa', 'visa cancellation'], requiredDocs: ['PASSPORT_COPY', 'EMIRATES_ID', 'VISA_PAGE'] },
    // ICA — Emirates ID (2)
    { templateKey: 'UAE-EID-NEW', name: 'Emirates ID - New', category: 'ICA', serviceChargeIncl: 178.50, govtCharge: 0, description: 'New Emirates ID application', aliases: ['new eid', 'new emirates id'], requiredDocs: ['PASSPORT_COPY', 'PHOTO', 'VISA_PAGE'] },
    { templateKey: 'UAE-EID-RENEW', name: 'Emirates ID - Renewal', category: 'ICA', serviceChargeIncl: 178.50, govtCharge: 0, description: 'Emirates ID renewal', aliases: ['eid renewal', 'renew eid', 'emirates id renewal'], requiredDocs: ['PASSPORT_COPY', 'EMIRATES_ID'] },
    // MOHRE — Labour / TASHEEL (5)
    { templateKey: 'UAE-LABOUR-NEW', name: 'Labour Card - New', category: 'MOHRE', serviceChargeIncl: 210.00, govtCharge: 0, description: 'New labour card / work permit', aliases: ['new labour card', 'new labor card', 'new work permit'], requiredDocs: ['PASSPORT_COPY', 'VISA_PAGE', 'OFFER_LETTER'] },
    { templateKey: 'UAE-LABOUR-RENEW', name: 'Labour Card - Renewal', category: 'MOHRE', serviceChargeIncl: 157.50, govtCharge: 0, description: 'Labour card renewal', aliases: ['renew labour card', 'labour renewal', 'labor renewal'], requiredDocs: ['PASSPORT_COPY', 'EMIRATES_ID', 'LABOUR_CONTRACT'] },
    { templateKey: 'UAE-CONTRACT-CHANGE', name: 'Labour Contract Change', category: 'MOHRE', serviceChargeIncl: 105.00, govtCharge: 0, description: 'Contract/designation/salary change', aliases: ['contract change', 'designation change', 'salary change'], requiredDocs: ['PASSPORT_COPY', 'EMIRATES_ID', 'LABOUR_CONTRACT'] },
    { templateKey: 'UAE-TASHEEL', name: 'TASHEEL Services', category: 'MOHRE', serviceChargeIncl: 52.50, govtCharge: 0, description: 'MOHRE TASHEEL services', aliases: ['tasheel', 'mohre', 'tasheel services'] },
    { templateKey: 'UAE-JOB-OFFER', name: 'Job Offer Letter', category: 'MOHRE', serviceChargeIncl: 105.00, govtCharge: 0, description: 'Job offer letter processing', aliases: ['job offer', 'offer letter', 'job offer letter'], requiredDocs: ['PASSPORT_COPY'] },
    // MEDICAL — DHA/EHS (1)
    { templateKey: 'UAE-MEDICAL', name: 'Medical Test', category: 'MEDICAL', serviceChargeIncl: 0, govtCharge: 220.00, description: 'Medical fitness test', aliases: ['medical', 'medical test', 'medical checkup', 'fitness test', 'health card'], requiredDocs: ['PASSPORT_COPY', 'PHOTO'] },
    // MOFA (1)
    { templateKey: 'UAE-MOFA', name: 'MOFA Attestation', category: 'MOFA', serviceChargeIncl: 157.50, govtCharge: 0, description: 'MOFA document attestation', aliases: ['mofa', 'attestation', 'mofa stamping'], requiredDocs: ['PASSPORT_COPY'] },
    // TAWJEEH (1)
    { templateKey: 'UAE-TAWJEEH', name: 'TAWJEEH', category: 'TAWJEEH', serviceChargeIncl: 52.50, govtCharge: 0, description: 'TAWJEEH orientation card', aliases: ['tawjeeh', 'orientation', 'tawjeeh card'], requiredDocs: ['PASSPORT_COPY', 'PHOTO'] },
    // ADMIN (7)
    { templateKey: 'UAE-ESTAB-CARD', name: 'Establishment Card', category: 'ADMIN', serviceChargeIncl: 210.00, govtCharge: 0, description: 'Company establishment card', aliases: ['establishment card', 'company card'], requiredDocs: ['TRADE_LICENSE'] },
    { templateKey: 'UAE-WPS', name: 'WPS Registration', category: 'ADMIN', serviceChargeIncl: 105.00, govtCharge: 0, description: 'WPS salary protection registration', aliases: ['wps', 'wps registration', 'salary protection'], requiredDocs: ['TRADE_LICENSE', 'ESTABLISHMENT_CARD'] },
    { templateKey: 'UAE-EJARI', name: 'Ejari Registration', category: 'ADMIN', serviceChargeIncl: 52.50, govtCharge: 0, description: 'Ejari tenancy contract registration', aliases: ['ejari', 'tenancy contract registration', 'ejari registration'], requiredDocs: ['TENANCY_CONTRACT', 'TRADE_LICENSE'] },
    { templateKey: 'UAE-TRADE-NEW', name: 'Trade License - New', category: 'ADMIN', serviceChargeIncl: 315.00, govtCharge: 0, description: 'New trade license application', aliases: ['new trade license', 'business license'], requiredDocs: ['PASSPORT_COPY', 'TENANCY_CONTRACT'] },
    { templateKey: 'UAE-TRADE-RENEW', name: 'Trade License - Renewal', category: 'ADMIN', serviceChargeIncl: 210.00, govtCharge: 0, description: 'Trade license renewal', aliases: ['renew trade license', 'trade license renewal'], requiredDocs: ['TRADE_LICENSE'] },
    { templateKey: 'UAE-INSURANCE', name: 'Insurance', category: 'ADMIN', serviceChargeIncl: 105.00, govtCharge: 0, description: 'Health/worker insurance', aliases: ['insurance', 'health insurance', 'worker insurance'], requiredDocs: ['PASSPORT_COPY', 'EMIRATES_ID'] },
    { templateKey: 'UAE-TENANCY', name: 'Tenancy Contract', category: 'ADMIN', serviceChargeIncl: 52.50, govtCharge: 0, description: 'Tenancy contract attestation', aliases: ['tenancy contract', 'rent contract', 'tenancy attestation'], requiredDocs: ['TRADE_LICENSE'] },
    // OTHER (3)
    { templateKey: 'UAE-TYPING', name: 'Typing Services', category: 'OTHER', serviceChargeIncl: 21.00, govtCharge: 0, description: 'General typing services', aliases: ['typing', 'document typing'] },
    { templateKey: 'UAE-DOC-CLEAR', name: 'Document Clearing', category: 'OTHER', serviceChargeIncl: 52.50, govtCharge: 0, description: 'Document clearing/processing', aliases: ['document clearing', 'clearance'] },
    { templateKey: 'UAE-TRANSLATION', name: 'Translation', category: 'OTHER', serviceChargeIncl: 105.00, govtCharge: 0, description: 'Arabic/English translation', aliases: ['translation', 'translate', 'arabic translation'] }
  ],
  groups: [
    { templateKey: 'UAE-WF-ONBOARD', name: 'New Employee Onboarding', description: 'Full package for new hire setup', items: ['UAE-VISA-NEW', 'UAE-MEDICAL', 'UAE-EID-NEW', 'UAE-LABOUR-NEW', 'UAE-TAWJEEH'] },
    { templateKey: 'UAE-WF-RENEW', name: 'Visa Renewal Package', description: 'Annual visa renewal bundle', items: ['UAE-VISA-RENEW', 'UAE-MEDICAL', 'UAE-EID-RENEW'] },
    { templateKey: 'UAE-WF-EXIT', name: 'Employee Exit', description: 'Termination and exit processing', items: ['UAE-VISA-CANCEL', 'UAE-EXIT-VISA'] },
    { templateKey: 'UAE-WF-DOCS', name: 'Document Package', description: 'Document processing bundle', items: ['UAE-MOFA', 'UAE-TRANSLATION', 'UAE-DOC-CLEAR'] }
  ]
};

const ServiceTemplateDialog = ({ open, onClose, onImportComplete }) => {
  const theme = useTheme();
  const [existingKeys, setExistingKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState(null);

  // Editable charges state — init from template
  const [services, setServices] = useState([]);
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    if (open) {
      loadExistingKeys();
      initServices();
    }
  }, [open]);

  const loadExistingKeys = async () => {
    setLoading(true);
    try {
      const keys = await internalServiceService.getExistingTemplateKeys();
      setExistingKeys(keys);
    } catch (err) {
      console.error('Error loading existing template keys:', err);
    } finally {
      setLoading(false);
    }
  };

  const initServices = () => {
    const svcList = UAE_TEMPLATE.services.map(s => ({
      ...s,
      _serviceChargeIncl: s.serviceChargeIncl,
      _govtCharge: s.govtCharge
    }));
    setServices(svcList);
    // Select all by default (only non-existing ones will be importable)
    setSelected(svcList.map(s => s.templateKey));
  };

  const isExisting = (templateKey) => existingKeys.includes(templateKey);

  const selectableServices = services.filter(s => !isExisting(s.templateKey));
  const allSelectableSelected = selectableServices.length > 0 &&
    selectableServices.every(s => selected.includes(s.templateKey));

  const handleToggleAll = () => {
    if (allSelectableSelected) {
      setSelected([]);
    } else {
      setSelected(selectableServices.map(s => s.templateKey));
    }
  };

  const handleToggle = (templateKey) => {
    if (isExisting(templateKey)) return;
    setSelected(prev =>
      prev.includes(templateKey)
        ? prev.filter(k => k !== templateKey)
        : [...prev, templateKey]
    );
  };

  const handleChargeChange = (templateKey, field, value) => {
    setServices(prev => prev.map(s =>
      s.templateKey === templateKey
        ? { ...s, [field]: value === '' ? '' : parseFloat(value) || 0 }
        : s
    ));
  };

  const handleImport = async () => {
    setImporting(true);
    setError(null);
    try {
      const selectedServices = services
        .filter(s => selected.includes(s.templateKey))
        .map(s => ({
          templateKey: s.templateKey,
          name: s.name,
          category: s.category,
          description: s.description,
          serviceChargeIncl: s._serviceChargeIncl,
          govtCharge: s._govtCharge,
          aliases: s.aliases,
          requiredDocs: s.requiredDocs || [],
          customFields: UAE_DEFAULT_CUSTOM_FIELDS[s.templateKey] || []
        }));

      // Filter groups to only include those whose services are being imported or already exist
      const importedOrExistingKeys = new Set([
        ...selected,
        ...existingKeys
      ]);
      const applicableGroups = UAE_TEMPLATE.groups.filter(g =>
        g.items.some(key => importedOrExistingKeys.has(key))
      );

      const result = await internalServiceService.bulkImportTemplate({
        services: selectedServices,
        groups: applicableGroups
      });

      onImportComplete(result);
      onClose();
    } catch (err) {
      console.error('Error importing template:', err);
      setError(err?.response?.data?.message || 'Failed to import template');
    } finally {
      setImporting(false);
    }
  };

  const selectedCount = selected.length;
  const existingCount = services.filter(s => isExisting(s.templateKey)).length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" fontWeight={600}>Import UAE Standard Template</Typography>
            <Typography variant="body2" color="text.secondary">
              20 services with AI aliases + 4 service groups
            </Typography>
          </Box>
          <Box textAlign="right">
            <Chip
              label={`${selectedCount} selected`}
              color="primary"
              size="small"
              sx={{ mr: 1 }}
            />
            {existingCount > 0 && (
              <Chip
                label={`${existingCount} already imported`}
                color="default"
                size="small"
              />
            )}
          </Box>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 0 }}>
        {error && (
          <Alert severity="error" sx={{ m: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 500 }}>
            <Table size="small" stickyHeader>
              <TableHead sx={getBrandedTableHeadSx(theme)}>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allSelectableSelected}
                      indeterminate={selectedCount > 0 && !allSelectableSelected}
                      onChange={handleToggleAll}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>Service Name</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell align="right">Service Charge (Incl.)</TableCell>
                  <TableCell align="right">Govt Charge</TableCell>
                  <TableCell align="center">AI Aliases</TableCell>
                  <TableCell align="center">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {services.map((svc) => {
                  const existing = isExisting(svc.templateKey);
                  const isSelected = selected.includes(svc.templateKey);
                  return (
                    <TableRow
                      key={svc.templateKey}
                      hover={!existing}
                      sx={{ opacity: existing ? 0.5 : 1 }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isSelected || existing}
                          disabled={existing}
                          onChange={() => handleToggle(svc.templateKey)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{svc.name}</Typography>
                        {svc.description && (
                          <Typography variant="caption" color="text.secondary">
                            {svc.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip label={svc.category} size="small" sx={{ fontSize: '0.7rem' }} />
                      </TableCell>
                      <TableCell align="right">
                        {existing ? (
                          <Typography variant="body2">
                            {svc.serviceChargeIncl > 0 ? `AED ${svc.serviceChargeIncl.toFixed(2)}` : '-'}
                          </Typography>
                        ) : (
                          <TextField
                            size="small"
                            type="number"
                            value={svc._serviceChargeIncl}
                            onChange={(e) => handleChargeChange(svc.templateKey, '_serviceChargeIncl', e.target.value)}
                            disabled={existing}
                            inputProps={{ min: 0, step: 0.5, style: { textAlign: 'right', fontSize: '0.8rem', padding: '4px 8px' } }}
                            sx={{ width: 110 }}
                          />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {existing ? (
                          <Typography variant="body2">
                            {svc.govtCharge > 0 ? `AED ${svc.govtCharge.toFixed(2)}` : '-'}
                          </Typography>
                        ) : (
                          <TextField
                            size="small"
                            type="number"
                            value={svc._govtCharge}
                            onChange={(e) => handleChargeChange(svc.templateKey, '_govtCharge', e.target.value)}
                            disabled={existing}
                            inputProps={{ min: 0, step: 0.5, style: { textAlign: 'right', fontSize: '0.8rem', padding: '4px 8px' } }}
                            sx={{ width: 110 }}
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="caption" color="text.secondary">
                          {svc.aliases?.length || 0}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {existing ? (
                          <Chip
                            icon={<CheckIcon sx={{ fontSize: 14 }} />}
                            label="Imported"
                            size="small"
                            color="success"
                            sx={{ fontSize: '0.7rem' }}
                          />
                        ) : (
                          <Chip
                            label="New"
                            size="small"
                            color="info"
                            sx={{ fontSize: '0.7rem' }}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Groups summary */}
        <Box sx={{ p: 2, bgcolor: 'background.default' }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Service Groups (auto-created)
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            {UAE_TEMPLATE.groups.map(g => (
              <Chip
                key={g.templateKey}
                label={`${g.name} (${g.items.length} services)`}
                size="small"
                color="default"
                sx={{ fontSize: '0.75rem' }}
              />
            ))}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={importing}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={importing ? <CircularProgress size={16} /> : <ImportIcon />}
          onClick={handleImport}
          disabled={importing || selectedCount === 0}
        >
          {importing ? 'Importing...' : `Import ${selectedCount} Services`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ServiceTemplateDialog;
